import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import API from '../../API/Main/API';
import {
	MondaySyncLogProperties,
	MondaySyncDryRunResultProperties,
	MondaySyncRunProperties,
} from '../../API/ClientAPIHelpers/mondaySync';
import { ErrorProperties } from '../../API/identityAPIProperties';
import { resetState } from '../../resetState';

interface MondaySyncState {
	logs: MondaySyncLogProperties[];
	status: MondaySyncRunProperties | null;
	dryRunResult: MondaySyncDryRunResultProperties | null;
	loading: boolean;
	syncing: boolean;
	dryRunning: boolean;
	error: ErrorProperties;
}

const initialState: MondaySyncState = {
	logs: [],
	status: null,
	dryRunResult: null,
	loading: false,
	syncing: false,
	dryRunning: false,
	error: { status: 0, title: '' },
};

export const getMondaySyncLogs = createAsyncThunk('mondaySync/getLogs', async (take: number | undefined, thunkAPI) => {
	const response = await API.ClientAPI.MondaySync.getLogs(take);
	if (response.error) return thunkAPI.rejectWithValue(response.error);
	return response as MondaySyncLogProperties[];
});

export const getMondaySyncStatus = createAsyncThunk('mondaySync/getStatus', async (_, thunkAPI) => {
	const response = await API.ClientAPI.MondaySync.getStatus();
	if (response?.error) return thunkAPI.rejectWithValue(response.error);
	if (!response?.id) return null;

	return response as MondaySyncRunProperties;
});

// Read-only preview: starts a dry run in the background, then polls until the result is ready.
export const dryRunMonday = createAsyncThunk('mondaySync/dryRun', async (_, thunkAPI) => {
	// 1. Start the dry run (POST → 202).
	const startResponse = await API.ClientAPI.MondaySync.startDryRun();
	if (startResponse?.error) return thunkAPI.rejectWithValue(startResponse.error);

	// 2. Poll for the result every 5 seconds.
	const poll = async (): Promise<MondaySyncDryRunResultProperties> => {
		await new Promise((r) => {
			setTimeout(r, 5000);
		});
		const pollResponse = await API.ClientAPI.MondaySync.getDryRunResult();
		if (pollResponse?.error) return thunkAPI.rejectWithValue(pollResponse.error) as never;

		if (pollResponse?.status === 'running') return poll();
		if (pollResponse?.status === 'failed') return thunkAPI.rejectWithValue({ title: pollResponse.error }) as never;
		if (pollResponse?.status === 'completed') return pollResponse.result as MondaySyncDryRunResultProperties;

		// 204 / unexpected → treat as "not started"
		return thunkAPI.rejectWithValue({ title: 'Dry run returned no result.' }) as never;
	};

	return poll();
});

// Fire-and-return: the backend starts the sync in the background and responds immediately.
// Results are not returned here — they land in the sync log (refetched shortly after).
export const syncMondayNow = createAsyncThunk('mondaySync/syncNow', async (_, thunkAPI) => {
	const response = await API.ClientAPI.MondaySync.syncNow();
	if (response?.error) return thunkAPI.rejectWithValue(response.error);
	return response;
});

const mondaySyncSlice = createSlice({
	name: 'mondaySync',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		// getLogs
		builder.addCase(getMondaySyncLogs.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getMondaySyncLogs.fulfilled, (state, action) => {
			state.loading = false;
			state.logs = action.payload;
		});
		builder.addCase(getMondaySyncLogs.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getStatus (latest run) — silent (no global loading flag) so polling doesn't flicker the UI
		builder.addCase(getMondaySyncStatus.fulfilled, (state, action) => {
			state.status = action.payload;
		});

		// dryRun (read-only preview)
		builder.addCase(dryRunMonday.pending, (state) => {
			state.dryRunning = true;
			state.error = initialState.error;
		});
		builder.addCase(dryRunMonday.fulfilled, (state, action) => {
			state.dryRunning = false;
			state.dryRunResult = action.payload;
		});
		builder.addCase(dryRunMonday.rejected, (state, action) => {
			state.dryRunning = false;
			state.error = action.payload as ErrorProperties;
		});

		// syncNow (fire-and-return)
		builder.addCase(syncMondayNow.pending, (state) => {
			state.syncing = true;
			state.error = initialState.error;
		});
		builder.addCase(syncMondayNow.fulfilled, (state) => {
			state.syncing = false;
		});
		builder.addCase(syncMondayNow.rejected, (state, action) => {
			state.syncing = false;
			state.error = action.payload as ErrorProperties;
		});

		// reset
		builder.addCase(resetState, () => initialState);
	},
});

export default mondaySyncSlice.reducer;
